/**
 * FileDropzone — drag-and-drop or click-to-upload a single file.
 *
 * Parses PDF, DOCX, and plain text via `@talent-align/engine.parseFile`,
 * then hands back the raw text + source filename so the caller can stuff
 * it into the textarea AND keep the "loaded from file" label visible.
 */

import { useRef, useState } from "react";
import { FileText, Loader2, Upload, X } from "lucide-react";

import { parseFile, type ParseResult } from "@talent-align/engine";

interface Props {
  label: string;
  onParsed: (result: ParseResult) => void;
  accent?: "blue" | "purple";
}

type Status =
  | { kind: "idle" }
  | { kind: "loading"; name: string }
  | { kind: "done"; name: string; pageCount?: number; charCount: number }
  | { kind: "error"; message: string };

export function FileDropzone({ label, onParsed, accent = "blue" }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);

  async function handleFile(file: File) {
    setStatus({ kind: "loading", name: file.name });
    try {
      const result = await parseFile(file);
      onParsed(result);
      setStatus({
        kind: "done",
        name: result.source ?? file.name,
        pageCount: result.pageCount,
        charCount: result.text.length,
      });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Parse failed",
      });
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function clear() {
    setStatus({ kind: "idle" });
    if (inputRef.current) inputRef.current.value = "";
  }

  const accentBorder =
    accent === "purple"
      ? "focus:border-purple-500 hover:border-purple-500/50"
      : "focus:border-blue-500 hover:border-blue-500/50";

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`rounded-lg border-2 border-dashed px-4 py-3 transition ${
        dragOver
          ? "border-purple-500/60 bg-purple-500/5"
          : `border-slate-800/70 bg-slate-900/30 ${accentBorder}`
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      {status.kind === "idle" && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition"
        >
          <Upload className="w-4 h-4" />
          Drop a {label} here — PDF, DOCX, or text
        </button>
      )}

      {status.kind === "loading" && (
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <Loader2 className="w-4 h-4 animate-spin" />
          Parsing {status.name}...
        </div>
      )}

      {status.kind === "done" && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-emerald-300 min-w-0">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{status.name}</span>
            <span className="text-xs text-slate-500 flex-shrink-0">
              {status.pageCount ? `${status.pageCount}p · ` : ""}
              {status.charCount.toLocaleString()} chars
            </span>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-slate-500 hover:text-slate-300 transition"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {status.kind === "error" && (
        <div className="flex items-center justify-between gap-2 text-sm text-rose-300">
          <span>⚠ {status.message}</span>
          <button
            type="button"
            onClick={clear}
            className="text-slate-500 hover:text-slate-300 transition"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
